import { RootStateOrAny } from "react-redux";
import axios from "axios";
import translations from "Translation";
import { linkedSchemasOptionsProps } from "types";

let { _ } = translations("Kore");

const assignFiles = (formData: FormData, data, parentKey) => {
  if (data) {
    Object.keys(data).forEach((key) => {
      if (data[key] instanceof File) formData.append(parentKey ? `${parentKey}.${key}` : key, data[key]);
      else if (typeof data[key] === "object") assignFiles(formData, data[key], parentKey ? `${parentKey}.${key}` : key);
    });
  }
};

const removeLinkedPluginData = (dataToSave, keys) => {
  keys.forEach((key) => {
    delete dataToSave[key];
  });
};

const displayErrorMessage = (pluginName, error, requestType: string, dispatch, linkedPluginName?: string) => {
  let additionalError;

  if (error?.response?.data?.errors) {
    if (typeof error.response.data.errors === "object") additionalError = JSON.stringify(error.response.data.errors);
    else additionalError = error.response.data.errors;

    if (additionalError?.includes("must be unique")) additionalError = _("must be unique");
  }

  _ = translations(pluginName)._;

  dispatch.configurationPluginLoader.setStatusMessage(
    `${_(`An error has occured while ${requestType}`)}${linkedPluginName ? ` from the section ${_(linkedPluginName)}` : ""}${additionalError && ` : ${_(additionalError)}`}`,
    "error"
  );
};

interface ConfigurationPluginLoaderState {
  statusMessage: string;
  statusMessageType: string;
  isStatusMessageVisible: boolean;
  isSaving: boolean;
  isSaveEnabled: boolean;
  isInitialLoading: boolean;
  confirmationDialog: { confirmed?: boolean; show: boolean; message: string; jsonFormsPath: string };
}

export const configurationPluginLoader = {
  state: {
    statusMessage: "",
    statusMessageType: "info",
    isStatusMessageVisible: false,
    isSaving: false,
    isSaveEnabled: false,
    isInitialLoading: true,
    confirmationDialog: {
      show: false,
      confirmed: false,
      message: "",
      jsonFormsPath: ""
    }
  },
  reducers: {
    setStatusMessage: (currentState: ConfigurationPluginLoaderState, statusMessage: string, statusMessageType: string) => {
      const newConfigurationPluginLoader = { ...currentState, statusMessage, statusMessageType, isStatusMessageVisible: true };

      return newConfigurationPluginLoader;
    },
    hideStatusMessage: (currentState: ConfigurationPluginLoaderState) => {
      const newConfigurationPluginLoader = { ...currentState, isStatusMessageVisible: false };

      return newConfigurationPluginLoader;
    },
    setIsSaveEnabled: (currentState: ConfigurationPluginLoaderState, isSaveEnabled: boolean) => {
      const newConfigurationPluginLoader = { ...currentState, isSaveEnabled };

      return newConfigurationPluginLoader;
    },
    setIsSaving: (currentState: ConfigurationPluginLoaderState, isSaving: boolean) => {
      const newConfigurationPluginLoader = { ...currentState, isSaving };

      return newConfigurationPluginLoader;
    },
    setIsInitialLoading: (currentState: ConfigurationPluginLoaderState, isInitialLoading: boolean) => {
      const newConfigurationPluginLoader = { ...currentState, isInitialLoading };

      if (isInitialLoading) newConfigurationPluginLoader.isSaveEnabled = false;

      return newConfigurationPluginLoader;
    },
    setConfirmationDialog: (
      currentState: ConfigurationPluginLoaderState,
      { show, message, jsonFormsPath }: { show: string; message: string; jsonFormsPath: string }
    ) => {
      const newConfigurationPluginLoader = { ...currentState, confirmationDialog: { show, message, jsonFormsPath } };

      return newConfigurationPluginLoader;
    },
    setAcceptConfirmationDialog: (currentState: ConfigurationPluginLoaderState) => {
      const newConfigurationPluginLoader = { ...currentState, confirmationDialog: { ...currentState.confirmationDialog, confirmed: true } };

      return newConfigurationPluginLoader;
    },
    setCloseConfirmationDialog: (currentState: ConfigurationPluginLoaderState) => {
      const newConfigurationPluginLoader = {
        ...currentState,
        confirmationDialog: { ...currentState.confirmationDialog, show: false, confirmed: false, message: "", jsonFormsPath: "" }
      };

      return newConfigurationPluginLoader;
    }
  },
  effects: (dispatch) => {
    return {
      createOrUpdate: async (
        {
          pluginName,
          linkedSchemasOptions,
          reloadDynamicLabel
        }: { pluginName: string; dataAttributeName: string; linkedSchemasOptions: linkedSchemasOptionsProps[]; reloadDynamicLabel: boolean },
        rootState: RootStateOrAny
      ) => {
        dispatch.configurationPluginLoader.setIsSaveEnabled(false);
        dispatch.configurationPluginLoader.setIsSaving(true);

        let params = {};
        const apiPath = rootState.configurationPluginData.dataToSave.apiPath || pluginName;
        const id = rootState.configurationPluginData.dataToSave.primaryKeyValue;
        const foreignKey = rootState.configurationPluginData.dataToSave.foreignKey;
        const foreignKeyValue = rootState.configurationPluginData.dataToSave.foreignKeyValue;

        let dataToSave = { ...rootState.configurationPluginData.dataToSave.data };

        if (foreignKey && foreignKeyValue) dataToSave[foreignKey] = foreignKeyValue;

        const tempData = { ...dataToSave };
        const formData = new FormData();

        try {
          assignFiles(formData, tempData, null);

          if (!formData.keys().next().done) {
            formData.append("data", JSON.stringify(tempData));

            params = { headers: { "Content-Type": "multipart/form-data" } };
            dataToSave = formData;
          }

          if (linkedSchemasOptions?.length > 0) {
            linkedSchemasOptions.forEach((linkedSchemaOptions) => {
              removeLinkedPluginData(dataToSave, linkedSchemaOptions.dataSchemaKeysToDelete);
            });
          }

          let response;

          if (id) response = await axios.put(`${window.location.origin}/api/${apiPath}/${id}`, dataToSave, params);
          else response = await axios.post(`${window.location.origin}/api/${apiPath}`, dataToSave, params);

          dispatch.configurationPluginLoader.setStatusMessage(_("Successfully saved configurations"), "info");

          if (reloadDynamicLabel) dispatch.dynamicLabels.getData();

          return response?.data;
        } catch (error) {
          console.error(error);
          console.warn(
            `The readRow, updateRow or createRow functions are not implemented properly in the main.ts file for the plugin: ${pluginName}`
          );

          displayErrorMessage(pluginName, error, "saving", dispatch);

          dispatch.configurationPluginLoader.setIsSaveEnabled(true);

          return false;
        } finally {
          dispatch.configurationPluginLoader.setIsSaving(false);
        }
      },
      linkedPluginCreateOrUpdate: async ({ pluginName, linkedSchemasOptions, primaryKey, primaryKeyValue }, rootState: RootStateOrAny) => {
        dispatch.configurationPluginLoader.setIsSaveEnabled(false);
        dispatch.configurationPluginLoader.setIsSaving(true);

        let params = {};
        let dataToSave = rootState.configurationPluginData.dataToSave.data;
        const id = primaryKeyValue || dataToSave[primaryKey];
        const tempData = { ...dataToSave };
        const formData = new FormData();

        assignFiles(formData, tempData, null);

        if (!formData.keys().next().done) {
          formData.append("data", JSON.stringify(tempData));

          params = { headers: { "Content-Type": "multipart/form-data" } };
          dataToSave = formData;
        }

        try {
          if (id) await axios.put(`${window.location.origin}/api/${linkedSchemasOptions.pluginName}/linked/${pluginName}/${id}`, dataToSave, params);
          else await axios.post(`${window.location.origin}/api/${linkedSchemasOptions.pluginName}/linked/${pluginName}`, dataToSave, params);

          return true;
        } catch (error) {
          console.error(error);
          console.warn(
            `The ${`/api/${linkedSchemasOptions.pluginName}/linked/${pluginName}/${
              id ? id : ""
            }`} is not implemented properly in the main.ts file for the plugin: ${linkedSchemasOptions.pluginName}`
          );

          displayErrorMessage(pluginName, error, "saving", dispatch, linkedSchemasOptions.pluginName);

          dispatch.configurationPluginLoader.setIsSaveEnabled(true);

          return false;
        } finally {
          dispatch.configurationPluginLoader.setIsSaving(false);
        }
      },
      linkedPluginDelete: async ({ pluginName, linkedSchemasOptions, row }, rootState: RootStateOrAny) => {
        dispatch.configurationPluginLoader.setIsSaveEnabled(false);
        dispatch.configurationPluginLoader.setIsSaving(true);

        const primaryKey = rootState.configurationPluginData.dataToSave.primaryKey;
        const id = row && row[primaryKey];

        try {
          if (id) await axios.delete(`${window.location.origin}/api/${linkedSchemasOptions.pluginName}/linked/${pluginName}/${id}`);

          return true;
        } catch (error) {
          console.error(error);
          console.warn(
            `The ${`/api/${linkedSchemasOptions.pluginName}/linked/${pluginName}/${
              id ? id : ""
            }`} is not implemented properly in the main.ts file for the plugin: ${linkedSchemasOptions.pluginName}`
          );

          displayErrorMessage(pluginName, error, "deleting", dispatch, linkedSchemasOptions.pluginName);

          dispatch.configurationPluginLoader.setIsSaveEnabled(true);

          return false;
        } finally {
          dispatch.configurationPluginLoader.setIsSaving(false);
        }
      },
      fetch: async ({ url }: { url: string }) => {
        const { data } = await axios.get(`${window.location.origin}/api/${url}`);

        if (typeof data === "string" && data.includes("<!DOCTYPE html>")) {
          console.error(`Cannot fetch data from ${url}. Add the route to the main.ts file.`);

          return;
        }

        return data.results;
      },
      createOrUpdateDatatableRow: async (
        {
          newRow,
          row,
          pluginName,
          datatableApiPath,
          datatablePrimaryKey,
          reloadDynamicLabels
        }: { newRow: object; row: object; pluginName: string; datatableApiPath: string; datatablePrimaryKey: string; reloadDynamicLabels: boolean },
        rootState: RootStateOrAny
      ) => {
        const primaryKey = datatablePrimaryKey || rootState.configurationPluginData.dataToSave.primaryKey;
        let response = null;

        if (newRow[primaryKey] || newRow[primaryKey] === 0)
          response = await axios.put(`${window.location.origin}/api/${datatableApiPath || pluginName}/${newRow[primaryKey]}`, row);
        else response = await axios.post(`${window.location.origin}/api/${datatableApiPath || pluginName}`, row);

        if (reloadDynamicLabels) dispatch.dynamicLabels.getData();

        return response;
      },
      deleteDatatableRow: async (
        {
          row,
          pluginName,
          datatableApiPath,
          datatablePrimaryKey,
          reloadDynamicLabels
        }: { row: object; pluginName: string; datatableApiPath: string; datatablePrimaryKey: string; reloadDynamicLabels: boolean },
        rootState: RootStateOrAny
      ) => {
        try {
          const apiPath = datatableApiPath || rootState.configurationPluginData.dataToSave.apiPath;
          const primaryKey = datatablePrimaryKey || rootState.configurationPluginData.dataToSave.primaryKey;
          const primaryKeyValue = row && row[primaryKey];

          await axios.delete(`${window.location.origin}/api/${apiPath}/${primaryKeyValue}`);

          if (reloadDynamicLabels) dispatch.dynamicLabels.getData();

          return true;
        } catch (error) {
          console.error(error);
          console.warn(`The deleteRow function may not be implemented properly in the main.ts file for the plugin: ${pluginName}`);

          displayErrorMessage(pluginName, error, "deleting", dispatch);
        }
      },
      navigateToDatatableRow: (
        {
          row,
          location,
          navigate,
          search,
          datatablePrimaryKey
        }: { row: object; location: string; navigate: any; search?: string; datatablePrimaryKey: string },
        rootState: RootStateOrAny
      ) => {
        const primaryKey = datatablePrimaryKey || rootState.configurationPluginData.dataToSave.primaryKey;
        const id = row && row[primaryKey];

        navigate(`${location}/${id || "new"}${search || ""}`);
      },
      navigateToPreviousPage: ({ location, search, navigate }: { location: string; search?: string; navigate: any }, rootState: RootStateOrAny) => {
        const currentPath = location.split("/");

        currentPath.pop();

        // If the component has a foreign key, navigate a level up
        if (rootState.configurationPluginData.dataToSave.foreignKeyValue) currentPath.pop();

        const newPath = currentPath.join("/");

        navigate(newPath + search);
      }
    };
  }
};

export default configurationPluginLoader;
